// Core imports
import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useSearchParams } from 'react-router-dom';
import { t } from 'i18next';

// Component imports
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Form, Input, Popconfirm, Row, Select, Typography } from 'antd';

// Misc imports
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useCreatePredefinedResponseMutation, useDeletePredefinedResponseMutation, useUpdatePredefinedResponseMutation } from '../../store/predefinedEntities/predefinedEntitiesApi';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import { ADDITIONAL_ROLES_ORG, PERMISSIONS_ORG } from '../../constants/main';
import { useFetchOrganizationPredefinedResponsesQuery } from '../../store/liveChatSettings/liveChatSettingsApi';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../constants/regex';

// Constant declarations
const ADMIN_PERMISSIONS = [PERMISSIONS_ORG.ADMIN, ADDITIONAL_ROLES_ORG.LIVECHAT_ADMIN];

const PredefinedResponsesSidebarForm = ({
  selectedResponse,
  setSelectedResponse,
  onTriggerResponse
}) => {
  const [form] = Form.useForm();
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();

  const { currentData: activeConversationInfo } = getCurrentQueryState('fetchActiveConversation', { orgId, conversationId: searchParams.get('conversation') });
  const { data: predefinedResponses } = getCurrentQueryState('fetchPredefinedResponses', { orgId, urlParams: { tenantScope: activeConversationInfo?.tenantId } });
  const { data: organizationPredefinedResponses } = useFetchOrganizationPredefinedResponsesQuery({ orgId }, { skip: !orgId });

  const [deleteResponse] = useDeletePredefinedResponseMutation();
  const [createResponse, { isLoading: isCreatingPredefinedResponse }] = useCreatePredefinedResponseMutation();
  const [updateResponse, { isLoading: isUpdatingPredefinedResponse }] = useUpdatePredefinedResponseMutation();

  const userPermissions = useUserPermissions(orgId, activeConversationInfo?.tenantId);
  const hasAdminRights = ADMIN_PERMISSIONS.some(permission => userPermissions.includes(permission));

  useEffect(() => {
    form.setFieldsValue(
      selectedResponse?.id ? {
        responseName: selectedResponse.name,
        responseMessage: selectedResponse.text,
        categories: selectedResponse.categories || []
      } : {
        responseName: '',
        responseMessage: '',
        categories: []
      }
    );

  }, [selectedResponse?.id]);

  const onSubmitResponse = async (formValues) => {
    let valuesToSubmit = {
      text: formValues.responseMessage,
      name: formValues.responseName,
      categories: formValues.categories,
      organizationWide: false,
      tenantIds: [activeConversationInfo?.tenantId]
    };

    let isSaveSuccessful;
    if (selectedResponse?.id) {
      isSaveSuccessful = await updateResponse({ orgId, predefinedResponseId: selectedResponse.id, data: valuesToSubmit, isOrgLevel: false }).unwrap();
    } else {
      isSaveSuccessful = await createResponse({ orgId, data: valuesToSubmit, isOrgLevel: false }).unwrap();
    }

    if (isSaveSuccessful) {
      setSelectedResponse(null);
    }

    return;
  };

  const onDeleteResponse = async () => {
    if (selectedResponse) {
      deleteResponse({ orgId, predefinedResponseId: selectedResponse.id });
    }
    return;
  };

  const renderFormFooterButtons = () => {
    return (
      <Fragment>
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Button
              size="large"
              block
              onClick={() => setSelectedResponse(null)}
            >
              {t('Discard Changes')}
            </Button>
          </Col>

          <Col span={24}>
            <Button
              size="large"
              htmlType="submit"
              type="primary"
              block
              disabled={selectedResponse.organizationWide && !hasAdminRights}
              loading={isUpdatingPredefinedResponse|| isCreatingPredefinedResponse}
            >
              {t('Save Changes')}
            </Button>
          </Col>
        </Row>

        {
          (!selectedResponse.organizationWide && selectedResponse.id) || (selectedResponse.organizationWide && hasAdminRights) ? (
            <Row
              justify="center"
            >
              <Popconfirm
                title="Delete this response?"
                okText="Confirm"
                onConfirm={onDeleteResponse}
              >
                <Button
                  size="small"
                  className="fs-sm"
                  type="link"
                >
                  {t('Delete Response')}
                </Button>
              </Popconfirm>
            </Row>
          ) : null
        }
      </Fragment>
    );
  };

  const renderFormBody = () => {
    return (
      <Form
        form={form}
        name="predefinedResponsesSidebadForm"
        className="bg-layout border-radius-base px-4 pb-2 my-4"
        layout="vertical"
        onFinish={onSubmitResponse}
      >
        <Row className="mb-2">
          <Col span={24}>
            <Row justify={'space-between'} align={'middle'}>
              <Col>
                <Typography.Paragraph
                  className="fs-lg my-1 d-flex align-items-center"
                >
                  {selectedResponse.id ? t('Edit Canned Response') : t('Create Canned Response')}
                </Typography.Paragraph>
              </Col>

              <Col>
                <Button
                  hidden={!selectedResponse?.id}
                  type="link"
                  className="fs-lg"
                  onClick={() => onTriggerResponse(selectedResponse)}
                >
                  {t('Use it')}
                </Button>
              </Col>
            </Row>

            <Divider className="mt-0 mb-2"/>

            {renderFormFields()}
          </Col>

          <Col span={24} className="mt-4">
            {renderFormFooterButtons()}
          </Col>
        </Row>
      </Form>
    );
  };

  const renderFormFields = () => {
    return (
      <Fragment>
        <Form.Item
          name="responseName"
          label={t('Button Title')}
          tooltip={{
            icon: <QuestionCircleOutlined />,
            title: t('Enter a short title for the canned response button.')
          }}
          rules={[
            {
              required: true,
              message: t('Enter a name for this response')
            }
          ]}
        >
          <Input
            placeholder={t('Response name')}
            disabled={selectedResponse.organizationWide && !hasAdminRights}
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="responseMessage"
          className="mb-0"
          label={t('Message')}
          tooltip={{
            icon: <QuestionCircleOutlined />,
            title: t('Enter the canned response message. This is the message that will be sent to the user.')
          }}
          rules={[
            {
              required: true,
              message: t('Enter a message for this response')
            }
          ]}
        >
          <Input.TextArea
            placeholder={t('Response message')}
            size="large"
            disabled={selectedResponse.organizationWide && !hasAdminRights}
          />
        </Form.Item>
        <Form.Item
          className="mb-0"
          name="categories"
          label={t('Categories')}
          labelCol={{ span: 6 }}
          labelAlign="left"
        >
          <Select
            mode="tags"
            size="large"
            tokenSeparators={[',']}
            placeholder={t('Add a category or a list of categories')}
            // Options are all categories from all predefined responses
            options={
              (activeConversationInfo ? predefinedResponses : organizationPredefinedResponses || [])
                .map(response => response.categories) // Get all categories
                .flat() // Flatten the array to a single array
                .filter((category, index, self) => category && self.indexOf(category) === index) // Remove duplicates (and empty strings)
                .map(category => ({ label: category, value: category }))
            }
            disabled={!hasAdminRights}
            onInputKeyDown={(e) => {
              if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                e.preventDefault();
                return false;
              }
            }}
          />
        </Form.Item>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <Row>
        <Col span={24}>
          {renderFormBody()}
        </Col>
      </Row>
    </Fragment>
  );
};

PredefinedResponsesSidebarForm.propTypes = {
  selectedResponse: PropTypes.object,
  setSelectedResponse: PropTypes.func,
  onTriggerResponse: PropTypes.func
};

export default PredefinedResponsesSidebarForm;
